import React, { useMemo, useRef, useState, useEffect } from "react";
import * as Lucide from "lucide-react";
import { useDispatch } from "react-redux";
import { setDraggingComponent } from "@/store/iframeSlice";

const getIconFile = (iconName: string) => {
  return `import React from "react";
import { ${iconName} } from "lucide-react";

const DummyComponent = () => {
  return <${iconName} />;
};
  
export default DummyComponent;`;
};

export interface Props {
  icons: string[];
}

const LucideReactGallery = React.memo(({ icons }: Props) => {
  const dispatch = useDispatch();
  const [numIconsToShow, setNumIconsToShow] = useState(200);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && numIconsToShow < icons.length) {
          setNumIconsToShow((prev) => Math.min(prev + 100, icons.length));
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [icons.length, numIconsToShow]);

  const paginatedIcons = useMemo(() => {
    return icons.slice(0, numIconsToShow);
  }, [icons, numIconsToShow]);

  return (
    <div className="flex flex-row gap-2 px-2 row-gap-0 pr-0 overflow-y-auto flex-wrap content-start">
      {paginatedIcons.map((iconName, index) => {
        const IconComponent = Lucide[iconName];

        return (
          <div
            key={index}
            draggable={true}
            onDragStart={(e) => {
              e.dataTransfer.dropEffect = "move";

              dispatch(setDraggingComponent("lucide-icon"));
              e.dataTransfer.setData("tempo/contents", getIconFile(iconName));
            }}
            onDragEnd={() => {
              dispatch(setDraggingComponent(false));
            }}
            className="cursor-pointer relative w-8 h-8 p-1 shrink-0"
          >
            <IconComponent className="shrink-0 w-full h-full" />
          </div>
        );
      })}

      {/* Invisible element to trigger loading more icons */}
      {numIconsToShow < icons.length && (
        <div ref={loadMoreRef} className="h-4 w-full" />
      )}
    </div>
  );
});

export default LucideReactGallery;
